// Procedural hologram glyphs (docs/ART-DIRECTION.md §1, §2.2, §5): one low-poly silhouette per content key, keyed
// by UNIT_KEYS / BUILDING_KEYS / STRIKE_KEYS so a missing glyph is a compile error. Role reads by silhouette: land
// low and wide, air winged, sea a flat hull, structures vertical. These stand in until the Phase 6 GLB assets.
//
// Glyph space: 1 unit = one glyph size, base centre at the origin, +Y = surface normal (up), +Z = heading (forward).

import { BoxGeometry, ConeGeometry, CylinderGeometry, Euler, Matrix4, OctahedronGeometry, Quaternion, Vector3, type BufferGeometry } from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { STRIKES, STRIKE_KEYS, type BuildingKey, type StrikeKey, type TransportModeKey, type UnitKey } from '@taxwar/content'
import { InvariantViolation } from '@taxwar/net'

export interface GlyphDef {
  /** Builds the merged glyph geometry (called once per layer). */
  readonly build: () => BufferGeometry
  /** Size relative to the layer's base glyph size (per body for multi-body units). */
  readonly size: number
}

/** Missiles fly as glyphs; the other strikes resolve at once and never exist in flight. */
export type StrikeGlyph = { readonly kind: 'missile'; readonly glyph: GlyphDef } | { readonly kind: 'instant' }

const QUARTER_TURN = Math.PI / 2
const LOW_POLY_SEGMENTS = 6
const SQUARE_SEGMENTS = 4

type Triple = readonly [number, number, number]

interface Part {
  readonly geometry: BufferGeometry
  readonly at: Triple
  readonly rotate: Triple
}

const NO_ROTATION: Triple = [0, 0, 0]
/** Rotates +Y (three's cone/cylinder axis) onto +Z (glyph forward). */
const ALONG_FORWARD: Triple = [QUARTER_TURN, 0, 0]

function part(geometry: BufferGeometry, at: Triple, rotate: Triple = NO_ROTATION): Part {
  return { geometry, at, rotate }
}

const box = (w: number, h: number, d: number): BufferGeometry => new BoxGeometry(w, h, d)
const cone = (r: number, h: number, segments = SQUARE_SEGMENTS): BufferGeometry => new ConeGeometry(r, h, segments)
const tube = (r: number, h: number): BufferGeometry => new CylinderGeometry(r, r, h, LOW_POLY_SEGMENTS)

/** Places the parts and merges them into one non-indexed geometry (primitives mix indexed and non-indexed). */
function assemble(parts: readonly Part[]): BufferGeometry {
  const matrix = new Matrix4()
  const rotation = new Quaternion()
  const euler = new Euler()
  const position = new Vector3()
  const unitScale = new Vector3(1, 1, 1)
  const placed = parts.map(({ geometry, at, rotate }) => {
    rotation.setFromEuler(euler.set(rotate[0], rotate[1], rotate[2]))
    matrix.compose(position.set(at[0], at[1], at[2]), rotation, unitScale)
    const flat = geometry.index === null ? geometry : geometry.toNonIndexed()
    if (flat !== geometry) geometry.dispose()
    return flat.applyMatrix4(matrix)
  })
  const merged = mergeGeometries(placed, false)
  for (const geometry of placed) geometry.dispose()
  if (merged === null) throw new InvariantViolation('glyph parts do not share one attribute layout')
  return merged
}

function glyph(size: number, parts: () => readonly Part[]): GlyphDef {
  return { size, build: () => assemble(parts()) }
}

// ------------------------------------------------------------------------------------------------ units

export const UNIT_GLYPHS: Readonly<Record<UnitKey, GlyphDef>> = {
  conscripts: glyph(0.45, () => [part(cone(0.35, 0.7), [0, 0.35, 0]), part(box(0.5, 0.08, 0.5), [0, 0.04, 0])]),
  tank: glyph(1, () => [
    part(box(0.9, 0.3, 1.2), [0, 0.15, 0]),
    part(box(0.5, 0.22, 0.5), [0, 0.41, -0.05]),
    part(tube(0.05, 0.7), [0, 0.41, 0.55], ALONG_FORWARD),
  ]),
  artillery: glyph(1, () => [
    part(box(0.8, 0.25, 0.9), [0, 0.125, 0]),
    part(tube(0.07, 1.1), [0, 0.55, 0.3], [QUARTER_TURN * 0.6, 0, 0]),
  ]),
  mobile_sam: glyph(1, () => [
    part(box(0.8, 0.25, 1), [0, 0.125, 0]),
    part(tube(0.1, 0.7), [-0.18, 0.5, 0.05], [QUARTER_TURN * 0.45, 0, 0]),
    part(tube(0.1, 0.7), [0.18, 0.5, 0.05], [QUARTER_TURN * 0.45, 0, 0]),
  ]),
  fighter: glyph(1, () => [
    part(cone(0.15, 1.2, LOW_POLY_SEGMENTS), [0, 0.15, 0], ALONG_FORWARD),
    part(box(1.2, 0.04, 0.35), [0, 0.15, -0.1]),
    part(box(0.45, 0.04, 0.2), [0, 0.15, -0.5]),
    part(box(0.04, 0.3, 0.2), [0, 0.3, -0.5]),
  ]),
  bomber: glyph(1.2, () => [
    part(tube(0.15, 1.1), [0, 0.15, 0], ALONG_FORWARD),
    part(cone(0.15, 0.3, LOW_POLY_SEGMENTS), [0, 0.15, 0.7], ALONG_FORWARD),
    part(box(1.8, 0.06, 0.5), [0, 0.15, 0]),
    part(box(0.6, 0.05, 0.2), [0, 0.15, -0.5]),
  ]),
  drone_swarm: glyph(0.35, () => [part(new OctahedronGeometry(0.5, 0), [0, 0.5, 0])]),
  destroyer: glyph(1.1, () => [
    part(box(0.45, 0.18, 1.3), [0, 0.09, 0]),
    part(cone(0.225, 0.4), [0, 0.09, 0.85], ALONG_FORWARD),
    part(box(0.3, 0.25, 0.4), [0, 0.3, -0.1]),
  ]),
  carrier: glyph(1.4, () => [part(box(0.7, 0.12, 1.8), [0, 0.06, 0]), part(box(0.15, 0.35, 0.35), [0.25, 0.29, 0.1])]),
}

/**
 * Automatic transport craft (taxgw-8lvi, taxgw-rvwq), silhouetted apart from every unit glyph: a Port's boat is a
 * flat cargo hull; an Airbase's plane is a fat-bodied lifter with a high straight wing and a T-tail, never mistaken
 * for the swept bomber or the delta fighter.
 */
export const TRANSPORT_GLYPHS: Readonly<Record<TransportModeKey, GlyphDef>> = {
  sea: glyph(1, () => [
    part(box(0.6, 0.2, 1.5), [0, 0.1, 0]),
    part(cone(0.3, 0.5), [0, 0.1, 0.9], ALONG_FORWARD),
    part(box(0.45, 0.3, 0.45), [0, 0.35, -0.5]),
  ]),
  air: glyph(1.2, () => [
    part(box(0.34, 0.3, 1.3), [0, 0.2, 0]),
    part(cone(0.17, 0.3, LOW_POLY_SEGMENTS), [0, 0.2, 0.8], ALONG_FORWARD),
    part(box(1.7, 0.05, 0.32), [0, 0.37, 0.1]),
    part(tube(0.06, 0.3), [-0.45, 0.3, 0.2], ALONG_FORWARD),
    part(tube(0.06, 0.3), [0.45, 0.3, 0.2], ALONG_FORWARD),
    part(box(0.05, 0.42, 0.28), [0, 0.52, -0.55]),
    part(box(0.7, 0.04, 0.2), [0, 0.73, -0.6]),
  ]),
}

// ------------------------------------------------------------------------------------------------ buildings

export const BUILDING_GLYPHS: Readonly<Record<BuildingKey, GlyphDef>> = {
  tax_office: glyph(0.9, () => [part(box(0.5, 1.2, 0.5), [0, 0.6, 0]), part(cone(0.38, 0.35), [0, 1.375, 0])]),
  airbase: glyph(1, () => [
    part(box(1.2, 0.05, 0.35), [0, 0.025, 0]),
    part(tube(0.08, 0.7), [0.4, 0.35, 0.3]),
    part(box(0.22, 0.15, 0.22), [0.4, 0.75, 0.3]),
  ]),
  port: glyph(1, () => [
    part(box(1, 0.08, 0.3), [0, 0.04, 0]),
    part(box(0.1, 1, 0.1), [-0.2, 0.5, 0]),
    part(box(0.8, 0.07, 0.07), [0.15, 0.95, 0]),
  ]),
  sam_site: glyph(0.9, () => [
    part(tube(0.12, 0.6), [0, 0.3, 0]),
    part(cone(0.35, 0.15, LOW_POLY_SEGMENTS), [0, 0.68, 0]),
    part(tube(0.07, 0.6), [0.3, 0.3, 0], [0, 0, -QUARTER_TURN * 0.4]),
    part(tube(0.07, 0.6), [-0.3, 0.3, 0], [0, 0, QUARTER_TURN * 0.4]),
  ]),
  fortress: glyph(1, () => [
    part(box(0.8, 0.35, 0.8), [0, 0.175, 0]),
    part(tube(0.12, 0.8), [0.35, 0.4, 0.35]),
    part(tube(0.12, 0.8), [-0.35, 0.4, 0.35]),
    part(tube(0.12, 0.8), [0.35, 0.4, -0.35]),
    part(tube(0.12, 0.8), [-0.35, 0.4, -0.35]),
  ]),
  propaganda_office: glyph(0.9, () => [
    part(box(0.4, 0.6, 0.4), [0, 0.3, 0]),
    part(tube(0.03, 0.9), [0, 1.05, 0]),
    part(cone(0.2, 0.35, LOW_POLY_SEGMENTS), [0, 0.85, 0.2], ALONG_FORWARD),
  ]),
}

// ------------------------------------------------------------------------------------------------ strikes

const missile = (radius: number, length: number): readonly Part[] => [
  part(tube(radius, length), [0, 0, 0], ALONG_FORWARD),
  part(cone(radius, radius * 3, LOW_POLY_SEGMENTS), [0, 0, length / 2 + radius * 1.5], ALONG_FORWARD),
  part(box(radius * 4.5, 0.03, 0.15), [0, 0, -length / 2 + 0.05]),
  part(box(0.03, radius * 4.5, 0.15), [0, 0, -length / 2 + 0.05]),
]

export const STRIKE_GLYPHS: Readonly<Record<StrikeKey, StrikeGlyph>> = {
  precision_strike: { kind: 'missile', glyph: glyph(0.9, () => missile(0.08, 0.9)) },
  shock_and_awe: { kind: 'missile', glyph: glyph(1.1, () => missile(0.12, 1.1)) },
  sanctions: { kind: 'instant' },
  leaflet_drop: { kind: 'instant' },
}

// The glyph table must agree with content on which strikes fly (only missiles spawn strike entities).
for (const key of STRIKE_KEYS) {
  const flies = STRIKES[key].effect.kind === 'missile'
  if (flies !== (STRIKE_GLYPHS[key].kind === 'missile')) throw new InvariantViolation(`strike glyph for ${key} disagrees with content on whether it flies`)
}
